import Axios from "axios";
import Cookies from "js-cookie";
import React from "react";
import { LoadingDots } from "web-ui";

import type { Challenge, ChallengeStatus } from "../api/languageChallenge";
import { languageChallengeApi } from "../api/languageChallenge";
import type { User } from "../api/v2";
import { apiV2 } from "../api/v2";
import styles from "../styles/ErrorBoundedHomepage.module.scss";
import { parseJwt } from "../util/auth";
import { isExternalChallenge, isMegaChallenge } from "../util/challenges";
import { staticAssertNever } from "../util/types";

import ChallengeRun from "./ChallengeRun";
import Landing from "./Landing";
import LoggedOutPage from "./LoggedOutPage";
import NoAvailableChallengesPage from "./NoAvailableChallengesPage";
import RegistrationPage from "./RegistrationPage";
import TopBar from "./TopBar";
import UnrecoverableErrorPage from "./UnrecoverableErrorPage";

const JWT_COOKIE_NAME = "jwt_token";

interface Loading {
  tag: "loading";
}
interface ChallengeStatuses {
  tag: "challengeStatuses";
  challengeStatuses: [ChallengeStatus];
  user: User;
}
interface Registration {
  tag: "registration";
  shownChallenge: Challenge;
  user: User;
}
interface LoggedOut {
  tag: "loggedOut";
  shownChallenge: Challenge;
}

interface FatalError {
  tag: "fatalError";
}

interface NoAvailableChallenges {
  tag: "noAvailableChallenges";
}

type PageState =
  | Loading
  | ChallengeStatuses
  | Registration
  | LoggedOut
  | FatalError
  | NoAvailableChallenges;

const HomePage = () => {
  const [pageState, setPageState] = React.useState<PageState>({
    tag: "loading",
  });

  React.useEffect(() => {
    const jwt = Cookies.get(JWT_COOKIE_NAME);
    (async () => {
      const fetchedChallenges = (
        await languageChallengeApi.getChallenges()
      ).filter(challenge => !isExternalChallenge(challenge.id));
      // Uncomment the following to debug
      // const fetchedChallenges = SAMPLE_CHALLENGES;

      const fallbackChallengeToShow = fetchedChallenges[0];

      if (jwt) {
        const fetchedUser = await apiV2.getUser(parseJwt(jwt).payload.sub);
        const fetchedChallengeStatuses = await (async () => {
          try {
            return await languageChallengeApi.getStatuses();
          } catch (e) {
            if (Axios.isAxiosError(e) && e.response?.status === 404) {
              return null;
            } else {
              return undefined;
            }
          }
        })();
        // Uncomment the following to debug
        // eslint-disable-next-line no-console
        // console.log({fetchedChallengeStatuses1, fetchedChallenges1, fetchedUser1});
        // const fetchedUser = SAMPLE_USER;
        // const fetchedChallengeStatuses = SAMPLE_CHALLENGE_STATUSES;

        if (fetchedChallenges.length === 0) {
          setPageState({
            tag: "noAvailableChallenges",
          });
        } else if (
          !fetchedChallengeStatuses ||
          // If user is registered for a mega challenge but not a language challenge, we want to show the registration page.
          fetchedChallengeStatuses.every(status =>
            isMegaChallenge(status.challenge.id),
          )
        ) {
          setPageState({
            shownChallenge: fallbackChallengeToShow,
            tag: "registration",
            user: fetchedUser,
          });
        } else if (fetchedChallengeStatuses) {
          setPageState({
            challengeStatuses: fetchedChallengeStatuses,
            tag: "challengeStatuses",
            user: fetchedUser,
          });
        } else if (fetchedChallengeStatuses === undefined) {
          setPageState({
            tag: "fatalError",
          });
        }
      } else {
        setPageState({
          shownChallenge: fallbackChallengeToShow,
          tag: "loggedOut",
        });
      }
    })();
  }, []);

  const topBar = (() => {
    switch (pageState.tag) {
      case "loading":
      case "loggedOut":
      case "fatalError":
      case "noAvailableChallenges":
        return <TopBar />;
      case "challengeStatuses":
      case "registration":
        return <TopBar user={pageState.user} />;
      default:
        staticAssertNever(pageState);
        return null;
    }
  })();

  const registrationLanding = (() => {
    switch (pageState.tag) {
      case "loading":
        return <LoadingDots manualPositioning={true} type="screen-white" />;
      case "loggedOut":
      case "registration":
        return <Landing challenge={pageState.shownChallenge} />;
      case "challengeStatuses":
      case "fatalError":
      case "noAvailableChallenges":
        return null;
      default:
        staticAssertNever(pageState);
        return null;
    }
  })();

  const body = (() => {
    switch (pageState.tag) {
      case "loading":
        return (
          <div className={styles["loading-dots-container"]}>
            <LoadingDots manualPositioning={true} type="screen-white" />
          </div>
        );
      case "loggedOut":
        return <LoggedOutPage />;
      case "challengeStatuses":
        return pageState.challengeStatuses.map(challengeStatus => (
          <ChallengeRun
            challengeStatus={challengeStatus}
            key={challengeStatus.challenge.id}
            user={pageState.user}
          />
        ));
      case "registration":
        return (
          <RegistrationPage
            shownChallengeId={pageState.shownChallenge.id}
            user={pageState.user}
          />
        );
      case "noAvailableChallenges":
        return <NoAvailableChallengesPage />;
      case "fatalError":
        return <UnrecoverableErrorPage />;
      default:
        staticAssertNever(pageState);
        return null;
    }
  })();

  const qAndA = (() => null)();
  // switch (pageState.tag) {
  //     case "loading":
  //         return <LoadingDots manualPositioning={true} type="screen-white" />;
  //     case "loggedOut":
  //     case "registered":
  //     case "notRegistered":
  //         return <Landing challenge={pageState.shownChallenge} />;
  //     default:
  //         staticAssertNever(pageState);
  //         return null;
  // }

  return (
    <div className={styles.wrap}>
      {topBar}
      {registrationLanding}
      {body}
      {qAndA}
    </div>
  );
};
export default HomePage;
